import { motion } from "framer-motion";
import { resolveAssetUrl } from "@/lib/api";

/**
 * PhotoFrame — card-stack entrance + butterfly hover squares + offset saffron/navy frames
 */
export default function PhotoFrame({ src, alt = "", ratio = "4/5", float = false, className = "", testid }) {
  const url = resolveAssetUrl(src || "");
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85, y: 60, rotate: -6 }}
      whileInView={{ opacity: 1, scale: 1, y: 0, rotate: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
      whileHover="hover"
      className={`photo-frame group ${float ? "floaty" : ""} ${className}`}
      data-testid={testid}
    >
      {/* Card-stack ghost cards behind the photo */}
      <motion.span
        aria-hidden
        initial={{ rotate: -8, x: -24, y: 16, opacity: 0 }}
        whileInView={{ rotate: -6, x: -20, y: 14, opacity: 0.6 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
        className="pointer-events-none absolute inset-0 rounded-[1.75rem] bg-saffron/25 border border-saffron/40 z-0"
      />
      <motion.span
        aria-hidden
        initial={{ rotate: 8, x: 24, y: 16, opacity: 0 }}
        whileInView={{ rotate: 6, x: 20, y: 14, opacity: 0.55 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        className="pointer-events-none absolute inset-0 rounded-[1.75rem] bg-navy/15 border border-navy/30 z-0"
      />

      {/* Butterfly hover squares */}
      <motion.span variants={{ hover: { x: -28, y: -28, opacity: 1, rotate: -18 } }} initial={{ opacity: 0 }} transition={{ duration: 0.5 }}
        className="pointer-events-none absolute top-0 left-0 h-8 w-8 rounded-md bg-saffron/80 z-[4]"/>
      <motion.span variants={{ hover: { x: 28, y: -34, opacity: 1, rotate: 18 } }} initial={{ opacity: 0 }} transition={{ duration: 0.55 }}
        className="pointer-events-none absolute top-0 right-0 h-6 w-6 rounded-md bg-navy/80 z-[4]"/>
      <motion.span variants={{ hover: { x: -32, y: 28, opacity: 1, rotate: 22 } }} initial={{ opacity: 0 }} transition={{ duration: 0.6 }}
        className="pointer-events-none absolute bottom-0 left-0 h-7 w-7 rounded-md bg-navy/70 z-[4]"/>
      <motion.span variants={{ hover: { x: 30, y: 30, opacity: 1, rotate: -22 } }} initial={{ opacity: 0 }} transition={{ duration: 0.65 }}
        className="pointer-events-none absolute bottom-0 right-0 h-9 w-9 rounded-md bg-saffron/70 z-[4]"/>

      <div className="photo-frame__inner relative z-[3]" style={{ aspectRatio: ratio }}>
        {url ? (
          <img src={url} alt={alt} className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-[1.04]"/>
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-navy to-navy-600 grid place-items-center text-white/70 text-sm">No image</div>
        )}
      </div>
    </motion.div>
  );
}
